
/**
 * Module dependencies.
 */

import {
  FormDataProps,
  submitTalkToUsForm
} from 'src/api/app/forms/submit-talk-to-us-form';

import {
  FormGroup,
  HelpText
} from 'src/components/core/forms/fields-styled-components';

import { color, media, units } from 'src/styles/utils';
import { regexes } from 'src/core/utils/regexes';
import { resolvePageUrl } from 'src/core/utils/routes';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { useNewsletter } from 'src/api/app/forms/newsletter/use-newsletter';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Checkbox from 'src/components/core/checkbox';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import Input from 'src/components/core/forms/fields/input';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import PrivacyPolicyCheckboxLabel from 'src/components/core/forms/privacy-policy-checkbox-label';
import RawHtml from 'src/components/core/raw-html';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import Richtext from '../core/richtext';
import Select from 'src/components/core/forms/fields/select';
import Type from 'src/components/core/typography';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import useCountries from 'src/api/app/countries/use-countries';
import useDistricts from 'src/api/app/countries/use-districts';
import useGTMEvent from 'src/hooks/use-gtm-event';

/**
 * `Props` type.
 */

type Props = {
  description: string,
  imageUrl: string | null,
  isKiosk?: boolean,
  title: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  background-color: ${color('beige200')};
  overflow: hidden;
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding-bottom: ${units(25)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'image'
    'form';
  grid-template-columns: 100%;
  grid-template-rows: repeat(3, max-content);

  ${media.min('ms')`
    grid-template-areas:
      '. title title .'
      '. form  .     image'
      '. form  .     .';
    grid-template-columns: 1fr 4fr 1fr 6fr;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  grid-area: title;
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  ${media.min('ms')`
    margin-bottom: ${units(5.5)};
  `}
`;

/**
 * `FormWrapper` styled component.
 */

const FormWrapper = styled.div`
  grid-area: form;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  margin-bottom: ${units(6)};
  padding-bottom: 117%;
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  ${media.min('ms')`
    margin-bottom: 0;
  `}
`;

/**
 * `StyledFormGroup` styled component.
 */

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: ${units(2)};
`;

/**
 * `StyledCheckbox` styled component.
 */

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: ${units(6)};
`;

/**
 * `ContactFormSection` component.
 */

const ContactFormSection = (props: Props): ReactElement => {
  const { locale, translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const {
    description,
    imageUrl,
    isKiosk,
    title
  } = props;

  const formRef = useRef<HTMLDivElement>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>();
  const {
    data: countriesData,
    isLoading: isLoadingCountries
  } = useCountries();

  const {
    data: districtsData,
    isLoading: isLoadingDistricts
  } = useDistricts();

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    reset,
    watch
  } = useForm({ mode: 'onBlur' });

  const sendGTMEvent = useGTMEvent();
  const watchCountry = watch('countryAlpha2', 'PT');
  const {
    isLoading: isLoadingSubmitForm,
    mutateAsync: submitForm
  } = useMutation((formData: FormDataProps) => {
    return submitTalkToUsForm({ formData, locale });
  }, {
    onError: () => {
      showMessage(translate('forms:messages.error'), { appearance: 'error' });
    },
    onSuccess: () => {
      showMessage(translate('forms:messages.success'), { appearance: 'success' });

      setIsSubmitted(true);
      reset();
      sendGTMEvent('contact', resolvePageUrl('contacts', locale));
      sendGTMEvent('contactsSuccess')

      // Manual reset of fields that use Controller.
      reset({
        countryAlpha2: '',
        districtId: ''
      });
    }
  });

  const {
    isSubmitting: isSubscribing,
    onSubscribeNewsletter
  } = useNewsletter({ hideMessages: true });

  const isSubmitting = (isLoadingSubmitForm ?? formState?.isSubmitting) || isSubscribing;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleGotoContactFormSection = useCallback(() => {
    if (formRef) {
      const formElement = formRef.current;
      const yPosition = formElement.getBoundingClientRect().top + window.pageYOffset - 140;

      window.scrollTo({
        behavior: 'smooth',
        top: yPosition
      });
    }
  }, []);

  const onSubmit = useCallback(async data => {
    const {
      company,
      countryAlpha2,
      districtId,
      email,
      isIndustryProfessional,
      message,
      name,
      phone,
      place,
      subject,
      subscribeNewsletter
    } = data;

    let recaptchaToken = await executeRecaptcha('contactForm').then(token => token);
    const normalizedData = {
      ...!isEmpty(company) && { company },
      ...!isEmpty(phone) && { phone },
      countryAlpha2: countryAlpha2?.value,
      email,
      isIndustryProfessional,
      message,
      name,
      recaptchaToken,
      subject,
      ...countryAlpha2?.value === 'PT' ? { districtId: districtId?.value } : { place }
    };

    submitForm(normalizedData).then(async () => {
      if (subscribeNewsletter) {
        recaptchaToken = await executeRecaptcha('contactForm').then(token => token);

        onSubscribeNewsletter({
          countryAlpha2: countryAlpha2?.value,
          email,
          name,
          professional: !!isIndustryProfessional,
          recaptchaToken
        });
      }
    });
  }, [executeRecaptcha, onSubscribeNewsletter, submitForm]);

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsSubmitted(false);

      handleGotoContactFormSection();
    }, 50);

    return () => clearTimeout(timeout);
  }, [handleGotoContactFormSection, isSubmitted]);

  return (
    <Section>
      <Container hasPadding={!isKiosk}>
        <Grid>
          <TitleWrapper>
            <FadeInUpAnimation>
              <Title>
                {title}
              </Title>

              <Richtext>
                {description}
              </Richtext>
            </FadeInUpAnimation>
          </TitleWrapper>

          <FormWrapper ref={formRef}>
            <ParallaxFadeInUp>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Input
                  disabled={isSubmitting}
                  error={errors?.name}
                  id={'contact-form-name'}
                  label={translate('forms:nameLabel')}
                  name={'name'}
                  placeholder={translate('forms:namePlaceholder')}
                  ref={register<any>({
                    required: {
                      message: translate('forms:requiredField'),
                      value: true
                    }
                  })}
                />

                <Input
                  disabled={isSubmitting}
                  error={errors?.email}
                  id={'contact-form-email'}
                  label={translate('forms:emailLabel')}
                  name={'email'}
                  placeholder={translate('forms:emailPlaceholder')}
                  ref={register<any>({
                    pattern: {
                      message: translate('forms:emailPatternValidation'),
                      value: regexes.email
                    },
                    required: {
                      message: translate('forms:requiredField'),
                      value: true
                    }
                  })}
                  type={'email'}
                />

                <Input
                  disabled={isSubmitting}
                  error={errors?.phone}
                  label={translate('forms:phoneNumberLabel')}
                  name={'phone'}
                  placeholder={translate('forms:phoneNumberPlaceholder')}
                  ref={register}
                />

                <Input
                  disabled={isSubmitting}
                  error={errors?.company}
                  label={translate('forms:companyNameLabel')}
                  name={'company'}
                  placeholder={translate('forms:companyNamePlaceholder')}
                  ref={register}
                />

                <Select
                  control={control}
                  disabled={isSubmitting}
                  error={errors?.countryAlpha2}
                  id={'contact-form-country'}
                  isLoading={isLoadingCountries}
                  label={translate('forms:countryLabel')}
                  name={'countryAlpha2'}
                  options={map(countriesData, ({ alpha2, name }) => ({
                    label: name,
                    value: alpha2
                  }))}
                  placeholder={translate('forms:selectPlaceholder')}
                  rules={{ required: {
                    message: translate('forms:requiredField'),
                    value: true
                  } }}
                />

                {watchCountry.value === 'PT' ? (
                  <Select
                    control={control}
                    disabled={isSubmitting}
                    error={errors?.districtId}
                    isLoading={isLoadingDistricts}
                    label={translate('forms:districtLabel')}
                    name={'districtId'}
                    options={map(districtsData, ({ id, name }) => ({
                      label: name,
                      value: id
                    }))}
                    placeholder={translate('forms:selectPlaceholder')}
                    rules={register<any>({
                      required: {
                        message: translate('forms:requiredField'),
                        value: true
                      }
                    })}
                  />
                ) : (
                  <Input
                    disabled={isSubmitting}
                    error={errors?.place}
                    label={translate('forms:districtLabel')}
                    name={'place'}
                    placeholder={translate('forms:districtPlaceholder')}
                    ref={register<any>({
                      required: {
                        message: translate('forms:requiredField'),
                        value: true
                      }
                    })}
                  />
                )}

                <Input
                  disabled={isSubmitting}
                  error={errors?.subject}
                  label={translate('forms:subjectLabel')}
                  name={'subject'}
                  placeholder={translate('forms:subjectPlaceholder')}
                  ref={register<any>({
                    required: {
                      message: translate('forms:requiredField'),
                      value: true
                    }
                  })}
                />

                <Input
                  as={'textarea'}
                  disabled={isSubmitting}
                  error={errors?.message}
                  label={translate('forms:messageTextareaLabel')}
                  name={'message'}
                  placeholder={translate('forms:messageTextareaPlaceholder')}
                  ref={register<any>({
                    required: {
                      message: translate('forms:requiredField'),
                      value: true
                    }
                  })}
                  rows={4}
                />

                <StyledFormGroup>
                  <Checkbox
                    alignment={'flex-start'}
                    disabled={isSubmitting}
                    id={'contact-form-construction-sector'}
                    label={
                      <RawHtml>
                        {translate('forms:constructionSectorCheckbox')}
                      </RawHtml>
                    }
                    name={'isIndustryProfessional'}
                    ref={register}
                  />
                </StyledFormGroup>

                <StyledFormGroup>
                  <Checkbox
                    alignment={'flex-start'}
                    disabled={isSubmitting}
                    id={'contact-form-privacy-policy'}
                    label={
                      <PrivacyPolicyCheckboxLabel
                        i18nKey={'forms:privacyPolicyContactsCheckbox'}
                        isKiosk={isKiosk}
                      />
                    }
                    name={'acceptPrivacyPolicy'}
                    ref={register<any>({
                      required: {
                        message: translate('forms:privacyPolicyError'),
                        value: true
                      }
                    })}
                  />

                  {errors?.acceptPrivacyPolicy && (
                    <HelpText hasError>
                      {errors?.acceptPrivacyPolicy.message}
                    </HelpText>
                  )}
                </StyledFormGroup>

                <StyledCheckbox
                  alignment={'flex-start'}
                  disabled={isSubmitting}
                  id={'contact-form-newsletter'}
                  label={translate('forms:newsletterCheckbox')}
                  name={'subscribeNewsletter'}
                  ref={register}
                />

                <Button
                  colorTheme={'primary'}
                  isLoading={isSubmitting}
                  type={'submit'}
                  variant={'fill'}
                >
                  {translate('forms:submit')}
                </Button>
              </form>
            </ParallaxFadeInUp>
          </FormWrapper>

          <ImageWrapper>
            {imageUrl && (
              <ParallaxFadeInUp
                parallaxOptions={{
                  percentage: 0.1,
                  speed: 2
                }}
              >
                <Image
                  alt={title}
                  layout={'fill'}
                  objectFit={'cover'}
                  src={imageUrl}
                />
              </ParallaxFadeInUp>
            )}
          </ImageWrapper>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `ContactFormSection` component.
 */

export default ContactFormSection;
